import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  composeSocialText,
  parseHashtags,
  SOCIAL_IMAGE_LIMITS,
  SOCIAL_JPEG_ONLY,
  SOCIAL_LABELS,
  SOCIAL_POST_LIMITS,
  SOCIAL_POST_STATUS_LABELS,
  SOCIAL_PROVIDERS,
  type IntegrationSummary,
  type SocialPost,
  type SocialPostImage,
  type SocialProvider,
} from '@kontor/shared';
import { api, ApiError, apiUpload } from '../../api';
import { useSession } from '../../session';
import './marketing.css';

const stamp = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/** Wert für ein Feld vom Typ datetime-local in der Zeitzone des Browsers */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Vorschlag für einen neuen geplanten Beitrag: morgen um 10 Uhr */
function tomorrowAtTen(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return localInput(d);
}

interface Draft {
  text: string;
  link: string;
  videoUrl: string;
  tags: string;
  images: SocialPostImage[];
  chosen: Set<SocialProvider>;
  when: 'now' | 'later';
  at: string;
}
const emptyDraft = (): Draft => ({ text: '', link: '', videoUrl: '', tags: '', images: [], chosen: new Set(), when: 'now', at: tomorrowAtTen() });

/** Was je gewähltem Netzwerk noch fehlt oder nicht passt. Der Server prüft dasselbe, hier sieht man es vor dem Absenden. */
function problems(d: Draft): string[] {
  const out: string[] = [];
  const { tags, invalid } = parseHashtags(d.tags);
  const hasImages = d.images.length > 0;
  if (invalid.length) out.push(`Kein gültiger Hashtag: ${invalid.join(', ')}. Erlaubt sind Buchstaben, Ziffern und der Unterstrich.`);
  if (tags.length > SOCIAL_POST_LIMITS.tags) out.push(`Höchstens ${SOCIAL_POST_LIMITS.tags} Hashtags.`);
  const full = composeSocialText({ text: d.text, link: d.link, tags, linkInText: true });
  for (const p of d.chosen) {
    const name = SOCIAL_LABELS[p];
    if (p === 'facebook' && !d.text.trim() && !d.link.trim() && !hasImages) out.push(`${name}: Text, Link oder Bild fehlt.`);
    if (p === 'instagram' && !hasImages) out.push(`${name}: Mindestens ein Bild fehlt.`);
    if (p === 'instagram' && full.length > SOCIAL_POST_LIMITS.instagramCaption) out.push(`${name}: ${full.length} von höchstens ${SOCIAL_POST_LIMITS.instagramCaption} Zeichen samt Link und Hashtags.`);
    if (p === 'linkedin' && !d.text.trim()) out.push(`${name}: Text fehlt.`);
    if (p === 'tiktok' && !hasImages && !d.videoUrl.trim()) out.push(`${name}: Bild oder Video fehlt.`);
    if (p === 'tiktok' && hasImages && d.videoUrl.trim()) out.push(`${name}: Entweder Bilder oder ein Video, nicht beides.`);
    if (SOCIAL_JPEG_ONLY.includes(p)) {
      const png = d.images.find((i) => i.contentType !== 'image/jpeg');
      if (png) out.push(`${name}: Nimmt nur JPG an, „${png.fileName}“ ist ein PNG.`);
    }
  }
  return out;
}

/**
 * Marketing → Beiträge (KON-193, KON-269): ein Beitrag mit Text, Hashtags, Bildern und Link geht sofort oder zu einem geplanten Zeitpunkt an die gewählten Netzwerke. Welche Netzwerke
 * verbunden sind, steht neben der Auswahl, verbunden wird unter „APIs und Webhooks“. Geplante Beiträge lassen sich bis zu ihrem Zeitpunkt ändern, vorziehen oder löschen.
 */
export function PostsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const mayPublish = can('marketing.send');
  const posts = useQuery({ queryKey: ['social-posts'], queryFn: async () => (await api<{ data: SocialPost[] }>('GET', '/marketing/posts')).data });
  // Die Übersicht der Verbindungen braucht das Recht „APIs und Webhooks“. Ohne es zeigt die Seite alle Netzwerke und der Server meldet, was fehlt.
  const integrations = useQuery({ queryKey: ['integrations'], queryFn: async () => (await api<{ data: IntegrationSummary[] }>('GET', '/integrations')).data, enabled: can('setup.integrations'), retry: false });
  const connected = (p: SocialProvider) => integrations.data?.find((i) => i.key === p)?.status === 'connected';
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const refresh = () => client.invalidateQueries({ queryKey: ['social-posts'] });
  const reset = () => {
    setDraft(emptyDraft());
    setEditing(null);
  };

  const body = () => ({
    text: draft.text || undefined,
    link: draft.link || undefined,
    videoUrl: draft.chosen.has('tiktok') && draft.videoUrl ? draft.videoUrl : undefined,
    tags: parseHashtags(draft.tags).tags,
    imageIds: draft.images.map((i) => i.id),
    providers: SOCIAL_PROVIDERS.filter((p) => draft.chosen.has(p)),
    ...(draft.when === 'later' ? { scheduledAt: new Date(draft.at).toISOString() } : {}),
  });

  const save = useMutation({
    mutationFn: async () =>
      editing ? (await api<{ data: SocialPost }>('PUT', `/marketing/posts/${editing}`, body())).data : (await api<{ data: SocialPost }>('POST', '/marketing/posts', body())).data,
    onSuccess: (post) => {
      refresh();
      client.invalidateQueries({ queryKey: ['storage'] });
      if (post.status === 'scheduled') {
        setMessage({ ok: true, text: `${editing ? 'Geändert. ' : ''}Der Beitrag ist geplant für ${stamp.format(new Date(post.scheduledAt!))} Uhr. Wirkia veröffentlicht ihn dann von selbst.` });
        reset();
        return;
      }
      const failed = post.targets.filter((t) => t.status === 'failed');
      setMessage(failed.length === 0 ? { ok: true, text: 'Der Beitrag ist veröffentlicht.' } : { ok: false, text: `Nicht überall hat es geklappt: ${failed.map((t) => `${SOCIAL_LABELS[t.provider]} (${t.error})`).join(' ')}` });
      reset();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const publishNow = useMutation({
    mutationFn: async (id: string) => (await api<{ data: SocialPost }>('POST', `/marketing/posts/${id}/publish`)).data,
    onSuccess: (post) => {
      refresh();
      const failed = post.targets.filter((t) => t.status === 'failed');
      setMessage(failed.length === 0 ? { ok: true, text: 'Der Beitrag ist veröffentlicht.' } : { ok: false, text: `Nicht überall hat es geklappt: ${failed.map((t) => `${SOCIAL_LABELS[t.provider]} (${t.error})`).join(' ')}` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Veröffentlichen hat nicht geklappt.') }),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api('POST', `/marketing/posts/${id}/retry`),
    onSuccess: refresh,
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Wiederholung hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/marketing/posts/${id}`),
    onSuccess: (_r, id) => {
      refresh();
      if (editing === id) reset();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  async function addFiles(files: FileList | null) {
    if (!files?.length) return;
    setMessage(null);
    const room = SOCIAL_POST_LIMITS.images - draft.images.length;
    const list = [...files].slice(0, Math.max(0, room));
    if (files.length > room) setMessage({ ok: false, text: `Ein Beitrag hat höchstens ${SOCIAL_POST_LIMITS.images} Bilder. Die übrigen wurden nicht hochgeladen.` });
    for (const file of list) {
      if (file.size > SOCIAL_IMAGE_LIMITS.maxBytes) {
        setMessage({ ok: false, text: `„${file.name}“ ist größer als 8 MB.` });
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const image = (await apiUpload<{ data: SocialPostImage }>('/marketing/posts/images', file)).data;
        setDraft((d) => ({ ...d, images: [...d.images, image] }));
      } catch (e) {
        setMessage({ ok: false, text: `„${file.name}“: ${errorText(e, 'Das Hochladen hat nicht geklappt.')}` });
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }

  function dropImage(image: SocialPostImage) {
    set({ images: draft.images.filter((i) => i.id !== image.id) });
    // Ein neu hochgeladenes Bild gleich wieder löschen. Eines, das schon zum geplanten Beitrag gehört, löscht der Server beim Speichern.
    if (!editing || !posts.data?.find((p) => p.id === editing)?.images.some((i) => i.id === image.id)) void api('DELETE', `/marketing/posts/images/${image.id}`).catch(() => undefined);
  }

  function moveFirst(image: SocialPostImage) {
    set({ images: [image, ...draft.images.filter((i) => i.id !== image.id)] });
  }

  function edit(p: SocialPost) {
    setMessage(null);
    setEditing(p.id);
    setDraft({
      text: p.text ?? '',
      link: p.link ?? '',
      videoUrl: p.videoUrl ?? '',
      tags: p.tags.map((t) => `#${t}`).join(' '),
      images: p.images,
      chosen: new Set(p.providers),
      when: 'later',
      at: localInput(new Date(p.scheduledAt ?? Date.now())),
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const toggle = (p: SocialProvider) =>
    setDraft((d) => {
      const chosen = new Set(d.chosen);
      if (chosen.has(p)) chosen.delete(p);
      else chosen.add(p);
      return { ...d, chosen };
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (draft.when === 'later' && !draft.at) {
      setMessage({ ok: false, text: 'Bitte wählen Sie Tag und Uhrzeit.' });
      return;
    }
    save.mutate();
  };

  const hints = problems(draft);
  const { tags } = parseHashtags(draft.tags);
  const planned = posts.data?.filter((p) => p.status === 'scheduled') ?? [];
  const done = posts.data?.filter((p) => p.status !== 'scheduled') ?? [];
  const busy = save.isPending || uploading > 0;

  return (
    <>
      <div className="page-head">
        <h1>Beiträge</h1>
      </div>
      <p className="muted">Veröffentlichen Sie Beiträge auf Ihren verbundenen Seiten, sofort oder zu einem geplanten Zeitpunkt. Die Netzwerke verbinden Sie unter <Link to="/einrichtung/api">Einrichtung → APIs und Webhooks</Link>, dort steht für jedes Netzwerk eine Anleitung.</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      {mayPublish && (
        <form ref={formRef} className="card post-form" onSubmit={submit} noValidate>
          <h2>{editing ? 'Geplanten Beitrag ändern' : 'Neuer Beitrag'}</h2>

          <fieldset className="field">
            <legend><span>Wohin?</span></legend>
            <div className="post-form__networks">
              {SOCIAL_PROVIDERS.map((p) => (
                <label key={p} className="check">
                  <input type="checkbox" checked={draft.chosen.has(p)} onChange={() => toggle(p)} />
                  <span>{SOCIAL_LABELS[p]}{integrations.data && !connected(p) ? <span className="muted small"> (noch nicht verbunden)</span> : null}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="field">
            <span>Text</span>
            <textarea className="input" rows={5} value={draft.text} onChange={(e) => set({ text: e.target.value })} maxLength={SOCIAL_POST_LIMITS.text} />
            <small className="muted">{draft.text.length} von {SOCIAL_POST_LIMITS.text} Zeichen. Instagram und TikTok erlauben 2200 Zeichen samt Hashtags.</small>
          </label>

          <label className="field">
            <span>Hashtags</span>
            <input className="input" value={draft.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="#herbst #weinfest" />
            <small className="muted">{tags.length > 0 ? <>Erscheint so am Ende des Beitrags: {tags.map((t) => `#${t}`).join(' ')}</> : 'Durch Leerzeichen oder Komma getrennt, die Raute ist freiwillig. Höchstens 30.'}</small>
          </label>

          <div className="field">
            <span>Bilder</span>
            {draft.images.length > 0 && (
              <ul className="post-images">
                {draft.images.map((image, i) => (
                  <li key={image.id} className="post-images__item">
                    <img src={image.url} alt={image.fileName} loading="lazy" />
                    <span className="small post-images__name" title={image.fileName}>{i === 0 ? 'Titelbild · ' : ''}{image.fileName}</span>
                    <span className="post-images__actions">
                      {i > 0 && <button type="button" className="btn btn--ghost btn--sm" onClick={() => moveFirst(image)}>Als Titelbild</button>}
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => dropImage(image)} aria-label={`${image.fileName} entfernen`}>Entfernen</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {draft.images.length < SOCIAL_POST_LIMITS.images && (
              <label className="btn btn--secondary post-images__add">
                {uploading > 0 ? 'Wird hochgeladen …' : draft.images.length ? 'Weitere Bilder hinzufügen' : 'Bilder hinzufügen'}
                <input type="file" accept="image/jpeg,image/png" multiple className="sr-only" disabled={uploading > 0} onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
              </label>
            )}
            <small className="muted">JPG oder PNG, höchstens 8 MB je Bild und {SOCIAL_POST_LIMITS.images} Bilder je Beitrag. Instagram und TikTok nehmen nur JPG an. Das erste Bild ist das Titelbild.</small>
          </div>

          <label className="field"><span>Link (freiwillig)</span><input className="input" type="url" value={draft.link} onChange={(e) => set({ link: e.target.value })} placeholder="https://" /><small className="muted">Ohne Bilder zeigen Facebook und LinkedIn eine Vorschau der Seite. Mit Bildern steht der Link im Text.</small></label>
          {draft.chosen.has('tiktok') && (
            <label className="field">
              <span>Adresse eines Videos (nur TikTok, statt Bildern)</span>
              <input className="input" type="url" value={draft.videoUrl} onChange={(e) => set({ videoUrl: e.target.value })} placeholder="https://…/video.mp4" />
              <small className="muted">TikTok holt das Video selbst ab. Die Adresse muss bei TikTok für Wirkia bestätigt sein.</small>
            </label>
          )}

          <fieldset className="field">
            <legend><span>Wann?</span></legend>
            <div className="post-form__when">
              <label className="check"><input type="radio" name="when" checked={draft.when === 'now'} onChange={() => set({ when: 'now' })} disabled={!!editing} /><span>Sofort veröffentlichen</span></label>
              <label className="check"><input type="radio" name="when" checked={draft.when === 'later'} onChange={() => set({ when: 'later' })} /><span>Planen</span></label>
              {draft.when === 'later' && (
                <input className="input post-form__at" type="datetime-local" value={draft.at} min={localInput(new Date(Date.now() + 2 * 60_000))} onChange={(e) => set({ at: e.target.value })} aria-label="Tag und Uhrzeit der Veröffentlichung" />
              )}
            </div>
            {draft.when === 'later' && <small className="muted">Wirkia veröffentlicht den Beitrag zu diesem Zeitpunkt von selbst, auch wenn niemand angemeldet ist. Bis dahin können Sie ihn ändern oder löschen.</small>}
          </fieldset>

          {draft.chosen.size > 0 && hints.length > 0 && (
            <div className="alert alert--warn" role="status">
              <strong>Bitte noch prüfen:</strong>
              <ul className="post-form__hints">{hints.map((h) => <li key={h}>{h}</li>)}</ul>
            </div>
          )}

          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={busy || draft.chosen.size === 0}>
              {save.isPending ? (draft.when === 'later' ? 'Wird gespeichert …' : 'Wird veröffentlicht …') : editing ? 'Änderungen speichern' : draft.when === 'later' ? 'Beitrag planen' : 'Jetzt veröffentlichen'}
            </button>
            {(editing || draft.text || draft.images.length > 0) && (
              <button className="btn btn--secondary" type="button" disabled={save.isPending} onClick={() => { if (!editing) draft.images.forEach((i) => void api('DELETE', `/marketing/posts/images/${i.id}`).catch(() => undefined)); reset(); setMessage(null); }}>
                {editing ? 'Abbrechen' : 'Verwerfen'}
              </button>
            )}
          </div>
        </form>
      )}

      <h2>Geplant</h2>
      {posts.isLoading && <p className="muted">Wird geladen …</p>}
      {posts.data && planned.length === 0 && <p className="muted">Keine geplanten Beiträge.</p>}
      <ul className="post-list">
        {planned.map((p) => (
          <PostCard key={p.id} post={p} editing={editing === p.id}>
            {mayPublish && (
              <div className="form-actions">
                <button className="btn btn--secondary btn--sm" type="button" onClick={() => edit(p)}>Bearbeiten</button>
                <button className="btn btn--secondary btn--sm" type="button" disabled={publishNow.isPending} onClick={() => { if (window.confirm('Den Beitrag jetzt sofort veröffentlichen?')) publishNow.mutate(p.id); }}>Jetzt veröffentlichen</button>
                <button className="btn btn--ghost btn--sm" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm('Den geplanten Beitrag löschen? Er wird dann nicht veröffentlicht.')) remove.mutate(p.id); }}>Löschen</button>
              </div>
            )}
          </PostCard>
        ))}
      </ul>

      <h2>Veröffentlicht</h2>
      {posts.data && done.length === 0 && <p className="muted">Noch keine Beiträge.</p>}
      <ul className="post-list">
        {done.map((p) => (
          <PostCard key={p.id} post={p} editing={false}>
            {mayPublish && p.status === 'done' && (
              <div className="form-actions">
                {p.targets.some((t) => t.status === 'failed') && <button className="btn btn--secondary btn--sm" type="button" disabled={retry.isPending} onClick={() => retry.mutate(p.id)}>Gescheiterte wiederholen</button>}
                <button className="btn btn--ghost btn--sm" type="button" onClick={() => { if (window.confirm('Den Eintrag aus der Liste löschen? Der Beitrag bleibt im Netzwerk bestehen.')) remove.mutate(p.id); }}>Aus der Liste löschen</button>
              </div>
            )}
          </PostCard>
        ))}
      </ul>
    </>
  );
}

/** Ein Beitrag in der Liste: Zeitpunkt, Text, Hashtags, Bilder und das Ergebnis je Netzwerk */
function PostCard({ post: p, editing, children }: { post: SocialPost; editing: boolean; children: ReactNode }) {
  const when = p.status === 'scheduled' ? p.scheduledAt : (p.publishedAt ?? p.createdAt);
  return (
    <li className={`card post${editing ? ' post--editing' : ''}`}>
      <p className="post__head small">
        <span className={`badge ${p.status === 'scheduled' ? 'badge--new' : p.targets.some((t) => t.status === 'failed') ? 'badge--rejected' : 'badge--won'}`}>{SOCIAL_POST_STATUS_LABELS[p.status]}</span>
        <span>{when ? `${stamp.format(new Date(when))} Uhr` : ''}</span>
        <span className="muted">{p.providers.map((x) => SOCIAL_LABELS[x]).join(', ')}</span>
      </p>
      {(p.text || p.videoUrl || p.link) && <p className="post__text">{p.text ?? p.videoUrl ?? p.link}</p>}
      {p.tags.length > 0 && <p className="small post__tags">{p.tags.map((t) => `#${t}`).join(' ')}</p>}
      {p.link && p.text && <p className="small muted">{p.link}</p>}
      {p.images.length > 0 && (
        <ul className="post-images post-images--small" aria-label="Bilder">
          {p.images.map((i) => <li key={i.id} className="post-images__item"><img src={i.url} alt={i.fileName} loading="lazy" /></li>)}
        </ul>
      )}
      {p.targets.length > 0 && (
        <ul className="post__targets small">
          {p.targets.map((t) => (
            <li key={t.provider} className={t.status === 'failed' ? 'post__failed' : ''}>
              <strong>{SOCIAL_LABELS[t.provider]}</strong>: {t.status === 'published' ? 'veröffentlicht' : `nicht geklappt. ${t.error ?? ''}`}
            </li>
          ))}
        </ul>
      )}
      {p.createdByName && <p className="small muted">Angelegt von {p.createdByName}</p>}
      {children}
    </li>
  );
}
